import React, { useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, PieChart, Pie, LabelList } from 'recharts';
import { Table2, BarChart3 } from 'lucide-react';

/* Validated categorical palettes (dataviz validator, dark surface #0d1322 / light #FFFFFF). */
export const PALETTE = {
  dark: ['#0891B2', '#8B5CF6', '#059669', '#D97706', '#DB2777', '#3B82F6'],
  light: ['#0891B2', '#7C3AED', '#047857', '#D97706', '#BE185D', '#1D4ED8']
};

const useScheme = (scheme) => scheme || 'dark';

const ChartTooltip = ({ active, payload, label, unit = '' }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  return (
    <div className="chart-tip">
      <span className="chart-tip-dot" style={{ background: p.payload.fill || p.color }} />
      <span className="chart-tip-label">{p.payload.name || label}</span>
      <span className="chart-tip-value">{p.value}{unit}</span>
    </div>
  );
};

const DataTable = ({ data, valueLabel = 'Value', unit = '' }) => (
  <div className="chart-table-wrap">
    <table className="chart-table">
      <thead><tr><th>Category</th><th>{valueLabel}</th></tr></thead>
      <tbody>
        {data.map((d) => <tr key={d.name}><td>{d.name}</td><td>{d.value}{unit}</td></tr>)}
      </tbody>
    </table>
  </div>
);

/** Frame with title, caption and chart/table toggle. */
export const ChartCard = ({ title, caption, children, data, unit, valueLabel, className = '' }) => {
  const [table, setTable] = useState(false);
  return (
    <div className={`chart-card ${className}`}>
      <div className="chart-head">
        <div>
          <h4 className="chart-title">{title}</h4>
          {caption && <p className="chart-caption">{caption}</p>}
        </div>
        {data && (
          <button type="button" className="chart-toggle" onClick={() => setTable((v) => !v)} aria-pressed={table} title={table ? 'Show chart' : 'Show table'}>
            {table ? <BarChart3 className="w-3.5 h-3.5" /> : <Table2 className="w-3.5 h-3.5" />}
            <span>{table ? 'Chart' : 'Table'}</span>
          </button>
        )}
      </div>
      {table && data ? <DataTable data={data} unit={unit} valueLabel={valueLabel} /> : children}
    </div>
  );
};

/** Horizontal bars — magnitude by category. One hue unless `categorical`. */
export const BarsH = ({ data, scheme, unit = '', categorical = false, height = 36 }) => {
  const s = useScheme(scheme);
  const colors = PALETTE[s];
  const rows = data.map((d, i) => ({ ...d, fill: categorical ? colors[i % colors.length] : colors[0] }));
  return (
    <div style={{ width: '100%', height: rows.length * height + 16 }}>
      <ResponsiveContainer>
        <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 44, bottom: 4, left: 4 }} barCategoryGap={8}>
          <XAxis type="number" hide domain={[0, 'dataMax']} />
          <YAxis type="category" dataKey="name" width={132} tickLine={false} axisLine={false} tick={{ fill: 'var(--text-secondary)', fontSize: 12, fontFamily: 'var(--font-body)' }} />
          <Tooltip cursor={{ fill: 'rgba(127,127,127,0.08)' }} content={<ChartTooltip unit={unit} />} />
          <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={12} isAnimationActive>
            {rows.map((r) => <Cell key={r.name} fill={r.fill} />)}
            <LabelList dataKey="value" position="right" formatter={(v) => `${v}${unit}`} style={{ fill: 'var(--text-primary)', fontSize: 12, fontFamily: 'var(--font-mono)' }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/** Donut — identity share with a hero number in the centre. */
export const Donut = ({ data, scheme, centerValue, centerLabel, size = 200 }) => {
  const s = useScheme(scheme);
  const colors = PALETTE[s];
  const rows = data.map((d, i) => ({ ...d, fill: colors[i % colors.length] }));
  const total = rows.reduce((a, b) => a + b.value, 0);
  return (
    <div className="donut">
      <div style={{ width: size, height: size, position: 'relative', flexShrink: 0 }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie data={rows} dataKey="value" nameKey="name" innerRadius={size * 0.34} outerRadius={size * 0.46} paddingAngle={3} stroke="var(--card-bg-solid, #0d1322)" strokeWidth={2} cornerRadius={4} isAnimationActive>
              {rows.map((r) => <Cell key={r.name} fill={r.fill} />)}
            </Pie>
            <Tooltip content={<ChartTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <div className="donut-center">
          <span className="donut-value">{centerValue ?? total}</span>
          {centerLabel && <span className="donut-label">{centerLabel}</span>}
        </div>
      </div>
      <ul className="chart-legend">
        {rows.map((r) => (
          <li key={r.name}>
            <span className="legend-swatch" style={{ background: r.fill }} />
            <span className="legend-name">{r.name}</span>
            <span className="legend-value">{r.value} · {Math.round((r.value / total) * 100)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Benefit matrix — tiers × benefits. */
export const TierMatrix = ({ tiers }) => {
  const benefits = Array.from(new Set(tiers.flatMap((t) => t.benefits)));
  return (
    <div className="chart-table-wrap">
      <table className="chart-table matrix">
        <thead>
          <tr>
            <th>Benefit</th>
            {tiers.map((t) => <th key={t.tier} className={t.highlighted ? 'hi' : ''}>{t.tier}</th>)}
          </tr>
        </thead>
        <tbody>
          {benefits.map((b) => (
            <tr key={b}>
              <td>{b}</td>
              {tiers.map((t) => (
                <td key={t.tier} className={`cell ${t.highlighted ? 'hi' : ''}`} aria-label={t.benefits.includes(b) ? 'Included' : 'Not included'}>
                  {t.benefits.includes(b) ? <span className="tick">✓</span> : <span className="dash">—</span>}
                </td>
              ))}
            </tr>
          ))}
          <tr className="matrix-foot">
            <td>Structure</td>
            {tiers.map((t) => <td key={t.tier} className={t.highlighted ? 'hi' : ''}>{t.equity}</td>)}
          </tr>
          <tr className="matrix-foot">
            <td>Range</td>
            {tiers.map((t) => <td key={t.tier} className={t.highlighted ? 'hi' : ''}>{t.range}</td>)}
          </tr>
        </tbody>
      </table>
    </div>
  );
};
